import React, { FC, memo, useEffect, useRef, useState } from 'react';
import { useHits, useInstantSearch, usePagination } from 'react-instantsearch';
import useCustomer from '~/features/commerce-api/hooks/useCustomer';
import { useBannerState } from '~/features/navigation/hooks/useBannerState';
import { isServer } from '~/lib/helpers/isServer';
import { TProductDetails } from '~/shared/components/ProductCard/ProductCard.definition';
import { EventIdentifier, useEvents } from '~/shared/hooks/useEvents';
import { _transformProductDetailsToDataLayer, getListName } from '~/shared/hooks/useEvents/helpers';
import { useFrame } from '~/shared/utils';
import { useTranslation } from '~/shared/utils/translation';
import { useGetTotalHits } from '~/shared/utils/useGetTotalHits';
import { usePage } from '~/templates/pages';
import { ProductList } from '../ProductList/ProductList';
import PageDropdown from './PageDropdown';
import { useRouter } from 'next/router';
import {
    FadeWrapper,
    LoadingOverlay,
    PaginationButton,
    PaginationControls,
    PaginationWrapper,
    PageDropdownWrapper,
    ProductListHits,
    ProductListWrapper,
} from './styled';
import styles from './PaginatedHits.module.scss';

interface PaginatedHitsProps {
    listId: EventIdentifier;
    itemsPerPage?: number;
    filterBarRef?: React.RefObject<HTMLElement>;
    productListRef?: React.RefObject<HTMLDivElement>;
    showStoreAvailability?: boolean;
}

const PaginatedHits: FC<PaginatedHitsProps> = ({
    listId,
    itemsPerPage = 20,
    filterBarRef,
    productListRef: productListRefProp,
    showStoreAvailability,
}) => {
    const { breadcrumb } = usePage();
    const { translate } = useTranslation();
    const { data: frame } = useFrame();
    const { productsImpressionsEvent } = useEvents(frame);
    const { setTotalHits } = useGetTotalHits();
    const localProductListRef = useRef<HTMLDivElement>(null);
    const productListRef = productListRefProp ?? localProductListRef;
    const { headerHeight } = useBannerState();
    const [isPaginating, setIsPaginating] = useState(false);
    const [contentHeight, setContentHeight] = useState<number | null>(null);
    const lastFireRef = useRef<{ listId: string; page: number; pathName: string } | null>(null);
    const prevButtonRef = useRef<HTMLButtonElement>(null);
    const nextButtonRef = useRef<HTMLButtonElement>(null);
    const { asPath } = useRouter();

    const { hits } = useHits<TProductDetails>();
    const {
        nbPages,
        nbHits: totalHits,
        refine: refinePage,
        currentRefinement: currentPage,
    } = usePagination();
    const { status } = useInstantSearch();
    const { isPlusMember } = useCustomer();

    const isLoading = status === 'loading' || status === 'stalled';
    const itemListName = breadcrumb ? getListName(breadcrumb) : '';

    useEffect(() => {
        // Reset pagination state and remove spacer when content finishes loading
        if (status === 'idle' && isPaginating) {
            setIsPaginating(false);
            // Clear the spacer height after a small delay to ensure smooth transition
            setTimeout(() => {
                setContentHeight(null);
            }, 100);
        }
    }, [status, isPaginating]);

    const handlePageChange = (newPage: number) => {
        // Capture current content height before pagination
        if (productListRef.current) {
            const currentHeight = productListRef.current.offsetHeight;
            setContentHeight(currentHeight);
        }

        // Start scroll animation during the expected Algolia response time
        if (filterBarRef?.current) {
            const rect = filterBarRef.current.getBoundingClientRect();
            // Default to 16px if server-side or if CSS var not available
            const gutterSize = !isServer
                ? parseInt(
                      getComputedStyle(document.documentElement).getPropertyValue(
                          '--plp-gutter-size',
                      ),
                  ) || 16
                : 16;
            const scrollOffset = headerHeight ?? 230;
            const targetPosition = window.scrollY + rect.top - scrollOffset - gutterSize;

            // Start scrolling after a short delay to utilize Algolia response time
            setTimeout(() => {
                window.scrollTo({
                    top: targetPosition,
                    behavior: 'smooth',
                });
            }, 300); // Use half of the expected 600ms Algolia/SearchProxy response time
        }

        setIsPaginating(true);
        refinePage(newPage);

        // We can use setTimeout with 0ms delay to ensure this happens after refinePage
        setTimeout(() => {
            if (prevButtonRef.current && currentPage > newPage) {
                prevButtonRef.current.blur();
            } else if (nextButtonRef.current && currentPage < newPage) {
                nextButtonRef.current.blur();
            }

            productListRef.current?.querySelectorAll('a')?.[0].focus({ preventScroll: true });
        }, 0);
    };

    const handlePrevious = () => {
        handlePageChange(Math.max(0, currentPage - 1));
    };

    useEffect(() => {
        const ready = status === 'idle' && hits.length > 0;
        if (!ready) return;

        const normalizedPath = asPath?.split('?')[0] ?? '';

        const last = lastFireRef.current;
        const changed =
            !last ||
            last.listId !== listId ||
            last.page !== currentPage ||
            last.pathName !== normalizedPath;

        if (!changed) return;

        productsImpressionsEvent(
            listId,
            itemListName,
            hits.map((hit, index) =>
                _transformProductDetailsToDataLayer(hit, currentPage, index, isPlusMember),
            ),
        );

        lastFireRef.current = {
            listId,
            page: currentPage,
            pathName: normalizedPath,
        };
    }, [asPath, hits, currentPage]);

    const handleNext = () => {
        handlePageChange(Math.min(currentPage + 1, nbPages - 1));
    };

    useEffect(() => {
        if (totalHits) setTotalHits(totalHits);
    }, [totalHits]);

    const renderPaginationControls = () => {
        if (nbPages <= 1 || hits.length === 0) return null;

        const allPages = Array.from({ length: nbPages }, (_, i) => i + 1);

        return (
            <PaginationControls
                className="pagination-controls"
                role="navigation"
                aria-label="Product list pagination"
            >
                <PaginationButton
                    ref={prevButtonRef}
                    color="secondary"
                    onClick={handlePrevious}
                    disabled={currentPage === 0}
                    aria-label={translate('plp.loadPreviousText')}
                    tabIndex={0}
                >
                    <span aria-hidden="true">&larr;</span>
                    <span className="pagination-text">{translate('plp.loadPreviousText')}</span>
                    {isLoading && (
                        <LoadingOverlay>
                            <span aria-label="Loading">◝</span>
                        </LoadingOverlay>
                    )}
                </PaginationButton>

                <PageDropdownWrapper>
                    <PageDropdown
                        currentPage={currentPage}
                        totalPages={nbPages}
                        pages={allPages}
                        onPageChange={handlePageChange}
                        showCheckmark={false}
                        aria-label={`Select page, current page ${currentPage + 1} of ${nbPages}`}
                    />
                </PageDropdownWrapper>

                <PaginationButton
                    ref={nextButtonRef}
                    color="secondary"
                    onClick={handleNext}
                    disabled={currentPage === nbPages - 1}
                    aria-label={translate('plp.loadMoreText')}
                    tabIndex={0}
                >
                    <span className="pagination-text">
                        {translate('plp.loadMoreText', { amount: itemsPerPage.toString() })}
                    </span>
                    <span aria-hidden="true">&rarr;</span>
                    {isLoading && (
                        <LoadingOverlay>
                            <span aria-label="Loading">◝</span>
                        </LoadingOverlay>
                    )}
                </PaginationButton>
            </PaginationControls>
        );
    };

    return (
        <div className={styles.paginatedHits}>
            {!!totalHits && (
                <ProductListHits
                    totalHits={totalHits}
                    showStoreAvailability={showStoreAvailability}
                />
            )}

            <ProductListWrapper
                ref={productListRef}
                className={styles.productListContainer}
                style={{
                    minHeight: isPaginating && contentHeight ? contentHeight : 'auto',
                }}
            >
                <FadeWrapper isLoading={isLoading}>
                    <ProductList hits={hits} listId={listId} itemsPerPage={itemsPerPage} />
                </FadeWrapper>

                <PaginationWrapper>{renderPaginationControls()}</PaginationWrapper>
            </ProductListWrapper>
        </div>
    );
};

export default memo(PaginatedHits);
